import type { ProjectPlanningDetail, ProjectWorkOrderDetail } from './types';

type WorkOrderStatus = ProjectWorkOrderDetail['work_order_current_status'];

const WORK_ORDER_STATUS_VALUES: readonly WorkOrderStatus[] = [
  'draft',
  'redrafting',
  'scheduled',
  'in_progress',
  'complete',
  'archived',
];

function isWorkOrderStatus(value: string): value is WorkOrderStatus {
  return (WORK_ORDER_STATUS_VALUES as readonly string[]).includes(value);
}

/**
 * Result shape returned by the reopen-for-edit / re-release endpoints. Each row
 * carries only its id and the (string) status the backend now holds — the
 * endpoints flip `is_redrafting` / status and never renumber sequences, so a
 * targeted in-place cache patch is safe.
 */
export interface StatusTransitionResult {
  updated: { work_order_id: string; work_order_current_status: string }[];
  unchanged: { work_order_id: string; work_order_current_status: string }[];
}

/**
 * Split work-order ids into fixed-size chunks (each `<= size`). Used to fan a
 * bulk status transition out into parallel requests, each within the backend
 * DTO's per-request cap. Empty input yields an empty array.
 */
export function chunkWorkOrderIds(ids: string[], size: number): string[][] {
  // A non-positive size would make `i += size` loop forever on non-empty input.
  if (size <= 0) {
    throw new Error('chunkWorkOrderIds: size must be a positive integer');
  }
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size));
  }
  return chunks;
}

/**
 * Patch a project detail's work orders with the statuses a transition returned.
 * Only rows whose status actually changes get a new object; every untouched row
 * keeps its reference identity so AG Grid's row diffing and the editor's row
 * memoisation stay stable.
 *
 * Returns `null` — a sentinel meaning "fall back to invalidate" — if any
 * returned status literal is outside the known union, so the caller refetches
 * server truth rather than trusting an unknown status.
 */
export function applyStatusTransitionResult(
  detail: ProjectPlanningDetail,
  result: StatusTransitionResult,
): ProjectPlanningDetail | null {
  const nextStatusById = new Map<string, WorkOrderStatus>();
  // Both buckets carry authoritative server state. `unchanged` is especially
  // important for idempotent retries / concurrent transitions: the local
  // cache may still hold the source status even though the server reports the
  // row was already at the requested target.
  for (const item of [...result.updated, ...result.unchanged]) {
    if (!isWorkOrderStatus(item.work_order_current_status)) {
      return null;
    }
    nextStatusById.set(item.work_order_id, item.work_order_current_status);
  }

  if (nextStatusById.size === 0) return detail;

  let changed = false;
  const workOrders = detail.work_orders.map((wo) => {
    const next = nextStatusById.get(wo.work_order_id);
    if (next === undefined || next === wo.work_order_current_status) return wo;
    changed = true;
    return { ...wo, work_order_current_status: next };
  });

  return changed ? { ...detail, work_orders: workOrders } : detail;
}
